import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import apiClient, { type Schemas, type User } from '@/api/client'
import { useApiRequest } from '@/composables/useApiRequest'
import { disconnectConsumer } from '@/realtime/cable'
import { type SessionCapabilities } from '@/services/rbac'
import { clearSentryUser, setSentryUser } from '@/services/sentry/user'
import { getCredential } from '@/services/webauthn'
import { upsertRemembered, useRememberedUsers } from '@/stores/remembered-users'
import { localTimezone } from '@/utils/datetime'
import type { PublicKeyCredentialWithAssertionJSON } from '@github/webauthn-json'

// PATCH /users/me: имя, часовой пояс и/или смена пароля (пара current_password +
// password связаны dependentRequired на уровне OpenAPI-схемы).
export interface UpdateMePatch {
  name?: string | null
  timezone?: string
  current_password?: string
  password?: string
}

export type OtpChallenge = Schemas['OtpChallenge']

// Активная имперсонация «Войти как» (E18): под кем работаем (impersonated) и
// кто настоящий админ (impersonator, для «Вернуться к себе»).
export type ImpersonationSession = Schemas['Impersonation']

// Обёртка ответов auth (E18): /auth/me, /auth/refresh, старт/стоп имперсонации.
type AuthSession = Schemas['AuthSession']

// Результат шага входа (§4.11). `ok` — сессия установлена (cookie выставлены).
// `otpRequired` — пароль верный, но нужен второй фактор: фронт уводит на 2FA-экран
// с этим challenge. `weakPassword` — ненавязчивое предупреждение после успешного входа.
export type LoginOutcome =
  | { ok: true; otpRequired: false; weakPassword: boolean }
  | { ok: false; otpRequired: true; challenge: OtpChallenge }
  | { ok: false; otpRequired: false; error?: unknown }

// Sentry-hint до /auth/me: первый запомненный юзер устройства (см. application.ts).
export function readLastUserHint(): Pick<User, 'id' | 'email' | 'name' | 'avatar_url'> | null {
  const first = useRememberedUsers().value.at(0)
  if (!first) return null
  return { id: first.id, email: first.email, name: first.name, avatar_url: first.avatar_url }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  // Права смотрящего от сервера: null — сессии нет, все гейты закрыты. shallowRef —
  // домен заменяется целиком (никто его не мутирует), глубокий прокси не нужен.
  const capabilities = shallowRef<SessionCapabilities | null>(null)
  const initialized = ref(false)
  const isAuthenticated = computed(() => user.value !== null)

  // Пояс смотрящего для презентации времени (§7.5, E213): всё показываемое время
  // приводится к персональному users.timezone. Под имперсонацией user — сотрудник,
  // значит время видно в его поясе. До init / без сессии — пояс браузера.
  const viewerTimezone = computed(() => user.value?.timezone ?? localTimezone())

  // Имперсонация «Войти как» (E18): когда админ работает под сотрудником, user —
  // это сотрудник, а impersonation хранит ссылку на настоящего админа. Заполняется
  // из /auth/me, login, refresh и старта/стопа.
  const impersonation = ref<ImpersonationSession | null>(null)
  const isImpersonating = computed(() => impersonation.value !== null)

  // Незавершённый 2FA-вход (§4.11): challenge живёт только в памяти между login
  // и 2FA-экраном. Не persist'им — challenge_token короткоживущий, refresh страницы
  // 2FA законно сбрасывает на /login (вход начинают заново).
  const pendingChallenge = ref<OtpChallenge | null>(null)

  // Запомненные на устройстве юзеры (мультивход): список карточек на экране входа.
  // Переживает logout — экран входа показывает, кто тут бывал. Реактивный список
  // поверх localStorage (useStorage) с автосинком при мутации; чистые операции —
  // в @/stores/remembered-users.
  const rememberedUsers = useRememberedUsers()

  // Upsert запомненного юзера. mergeCredentialId — passkey-ключ, использованный на
  // этом устройстве (логин/привязка): добавляем без дублей.
  function rememberUser(user: User, mergeCredentialId?: string) {
    rememberedUsers.value = upsertRemembered(rememberedUsers.value, user, mergeCredentialId)
  }

  // Привязка нового ключа на устройстве (usePasskeys): дописываем credential id
  // текущему запомненному юзеру, чтобы карточка предлагала вход по ключу.
  function addCredentialToRemembered(userId: number, credentialId: string) {
    const entry = rememberedUsers.value.find((u) => u.id === userId)
    if (!entry || entry.credential_ids.includes(credentialId)) return
    entry.credential_ids.push(credentialId)
  }

  // «Забыть на этом устройстве»: убрать карточку из списка экрана входа.
  function forgetRememberedUser(id: number) {
    rememberedUsers.value = rememberedUsers.value.filter((u) => u.id !== id)
  }

  // Все API-вызовы — через useApiRequest. Получаем единый паттерн с signal /
  // автоотменой при scope dispose / race-cancel при повторном execute.
  const fetchMeReq = useApiRequest((signal) =>
    apiClient.GET('/auth/me', { signal }),
  )
  const loginReq = useApiRequest(
    (signal, body: { email: string; password: string; remember_me: boolean }) =>
      apiClient.POST('/sessions', { body, signal }),
  )
  const logoutReq = useApiRequest((signal) =>
    apiClient.DELETE('/sessions', { signal }),
  )
  const updateMeReq = useApiRequest(
    (signal, patch: UpdateMePatch) =>
      apiClient.PATCH('/users/me', { body: { user: patch }, signal }),
  )
  // 2FA-шаги и восстановление пароля (§4.11) — все через useApiRequest, signal насквозь.
  const verifyOtpReq = useApiRequest(
    (signal, body: { challenge_token: string; code: string; remember_me: boolean }) =>
      apiClient.POST('/sessions/otp', { body, signal }),
  )
  const resendOtpReq = useApiRequest(
    (signal, challengeToken: string) =>
      apiClient.POST('/sessions/otp/resend', { body: { challenge_token: challengeToken }, signal }),
  )
  // Passkey-вход (passkeys): options ставит challenge-cookie на бэке, дальше
  // браузер подписывает, сервер верифицирует и выдаёт сессию. Тело options
  // опционально: пусто — usernameless, с challenge_token — второй фактор.
  const passkeyOptionsReq = useApiRequest(
    (signal, body: { challenge_token?: string; credential_ids?: string[] }) =>
      apiClient.POST('/sessions/passkey/options', { body, signal }),
  )
  const passkeyLoginReq = useApiRequest(
    (signal, credential: PublicKeyCredentialWithAssertionJSON) =>
      apiClient.POST('/sessions/passkey', {
        body: { credential: credential as unknown as Record<string, unknown> },
        signal,
      }),
  )
  const passkeyFactorReq = useApiRequest(
    (signal, challengeToken: string, credential: PublicKeyCredentialWithAssertionJSON) =>
      apiClient.POST('/sessions/passkey/verify', {
        body: { challenge_token: challengeToken, credential: credential as unknown as Record<string, unknown> },
        signal,
      }),
  )
  const requestResetReq = useApiRequest(
    (signal, email: string) =>
      apiClient.POST('/auth/password/reset', { body: { email }, signal }),
  )
  const updatePasswordReq = useApiRequest(
    (signal, body: { token: string; password: string }) =>
      apiClient.POST('/auth/password', { body, signal }),
  )
  // Добровольное переключение своей 2FA (§4.11): тумблер в профиле. Доступно
  // уровню 3 (RbacPolicy); endpoint тот же admin-механизм с id = свой.
  const twoFactorReq = useApiRequest(
    (signal, id: number, enabled: boolean) =>
      apiClient.PATCH('/users/{id}/two_factor', {
        params: { path: { id } },
        body: { enabled },
        signal,
      }),
  )
  // Имперсонация «Войти как» (E18): старт под сотрудником / возврат к себе.
  const startImpersonationReq = useApiRequest(
    (signal, userId: number) =>
      apiClient.POST('/impersonations', { body: { user_id: userId }, signal }),
  )
  const stopImpersonationReq = useApiRequest((signal) =>
    apiClient.DELETE('/impersonations/current', { signal }),
  )

  const loading = computed(() =>
    fetchMeReq.loading.value ||
    loginReq.loading.value ||
    logoutReq.loading.value ||
    updateMeReq.loading.value,
  )

  // Применяет состояние сессии {user, impersonation} (E18) из ответов auth.
  // remember — добавить юзера в карточки мультивхода (только при remember_me на
  // явном входе; восстановление сессии через fetchMe/refresh — тоже да, юзер тут
  // легитимно). Под имперсонацией НЕ запоминаем: карточки login-экрана — для этого
  // устройства (настоящий админ), а не сотрудник, под которым работаем.
  // Принимает не весь AuthSession, а ровно читаемые поля: login/2FA/passkey отдают
  // AuthResult, где те же права лежат рядом с профилем (#711).
  function applySession(
    session: {
      user: User
      impersonation: AuthSession['impersonation']
      capabilities?: SessionCapabilities | null
    },
    remember = true,
  ) {
    user.value = session.user
    capabilities.value = session.capabilities ?? null
    impersonation.value = session.impersonation ?? null
    if (remember && !session.impersonation) rememberUser(session.user)
    // Свежий Sentry user-context: ошибки привязываются к тому, под кем реально
    // работают (под имперсонацией — сотрудник), а не к hint'у на момент init.
    setSentryUser(session.user)
  }

  // Сессии нет: профиль, права и имперсонация обнуляются вместе — права от сервера
  // без юзера не имеют смысла, а остаточные открывали бы кнопки после разлогина.
  function clearSession() {
    user.value = null
    capabilities.value = null
    impersonation.value = null
    clearSentryUser()
  }

  async function fetchMe() {
    try {
      const response = await fetchMeReq.execute()
      if (response?.data) {
        applySession(response.data)
      } else {
        clearSession()
      }
    } catch {
      clearSession()
    } finally {
      initialized.value = true
    }
  }

  // Логин завершён: подменяем user (вход всегда без имперсонации). Общий хвост
  // login/verifyOtp/updateMyTwoFactor. remember — запомнить устройство (мультивход):
  // явный вход прокидывает remember_me, обновление своей сессии (2FA-тумблер) — true.
  // served передаётся ЯВНО каждым вызовом: дефолта нет, иначе на смене юзера (мультивход
  // с /login) новый юзер молча унаследовал бы права предыдущего. `null` = прав ещё нет.
  function completeSession(
    loggedIn: User,
    remember: boolean,
    served: SessionCapabilities | null | undefined,
  ) {
    applySession({ user: loggedIn, impersonation: null, capabilities: served }, remember)
  }

  async function login(
    email: string,
    password: string,
    rememberMe: boolean,
  ): Promise<LoginOutcome> {
    const response = await loginReq.execute({ email, password, remember_me: rememberMe })
    if (!response?.data) {
      return { ok: false, otpRequired: false, error: response?.error }
    }
    // Ответ логина обратносовместим: голый User (без 2FA) либо AuthResult
    // (двухшаговый flow, §4.11) — различаем по полю `status`.
    const data = response.data
    if (!('status' in data)) {
      // Legacy-форма прав не несёт — ставим null (fail-closed): гейты закрыты до
      // первого /auth/me, а не открыты по правам прежнего юзера.
      completeSession(data, rememberMe, null)
      return { ok: true, otpRequired: false, weakPassword: false }
    }
    if (data.status === 'otp_required' && data.challenge) {
      // Cookie НЕ выставлены — сессии ещё нет, юзер уходит на 2FA-экран.
      pendingChallenge.value = data.challenge
      return { ok: false, otpRequired: true, challenge: data.challenge }
    }
    if (data.user) completeSession(data.user, rememberMe, data.capabilities)
    return { ok: true, otpRequired: false, weakPassword: data.weak_password === true }
  }

  // Шаг 2FA (§4.11): верный код → полная сессия. Ошибки (otp_invalid / otp_expired /
  // otp_attempts_exhausted / otp_challenge_invalid) — в error, фронт показывает их.
  async function verifyOtp(challengeToken: string, code: string, rememberMe: boolean) {
    const response = await verifyOtpReq.execute({
      challenge_token: challengeToken,
      code,
      remember_me: rememberMe,
    })
    if (response?.data?.user) {
      pendingChallenge.value = null
      completeSession(response.data.user, true, response.data.capabilities)
      return { ok: true as const }
    }
    return { ok: false as const, error: response?.error }
  }

  // Повторная отправка кода (§4.11) → обновлённый challenge с таймером. 429 при
  // троттлинге (retry_after) приходит в error — экран показывает остаток.
  async function resendOtp(challengeToken: string) {
    const response = await resendOtpReq.execute(challengeToken)
    if (response?.data) {
      pendingChallenge.value = response.data
      return { ok: true as const, challenge: response.data }
    }
    return { ok: false as const, error: response?.error }
  }

  // Сброс незавершённого 2FA-входа (уход с экрана / возврат на /login).
  function clearChallenge() {
    pendingChallenge.value = null
  }

  // Переключение своей 2FA из профиля (§4.11): опциональна всем ролям.
  async function updateMyTwoFactor(enabled: boolean) {
    if (!user.value) return { ok: false as const, error: null }
    const response = await twoFactorReq.execute(user.value.id, enabled)
    if (response?.data) {
      // Своё же обновление (юзер и позиции те же) — ответ прав не несёт, держим текущие.
      completeSession(response.data, true, capabilities.value)
      return { ok: true as const }
    }
    return { ok: false as const, error: response?.error }
  }

  // Запрос ссылки восстановления (§4.11): всегда 202 (no enumeration). Успех —
  // когда ответ без ошибки (тело пустое).
  async function requestPasswordReset(email: string) {
    const response = await requestResetReq.execute(email)
    if (response && !response.error) {
      return { ok: true as const }
    }
    return { ok: false as const, error: response?.error }
  }

  // Установка пароля по ссылке (§4.11): token из письма + новый пароль → 204.
  // Ошибка (invalid_reset_token / validation_failed) — в error.
  async function updatePassword(token: string, password: string) {
    const response = await updatePasswordReq.execute({ token, password })
    if (response && !response.error) {
      return { ok: true as const }
    }
    return { ok: false as const, error: response?.error }
  }

  // Старт имперсонации (E18): админ входит под сотрудником. Ответ — сессия под
  // сотрудником ({user: сотрудник, impersonation: {impersonator: админ}}).
  async function startImpersonation(userId: number) {
    const response = await startImpersonationReq.execute(userId)
    if (response?.data) {
      applySession(response.data)
      // JWT-cookie сменился на сотрудника, но connection.rb идентифицирует
      // current_user один раз на connect — живой WS-сокет переживает смену cookie
      // и продолжает стримить настоящему админу, broadcast_to(сотрудник) до него
      // не доходит. Рвём явно: следующий getConsumer поднимет сокет с новым cookie
      // (тот же класс бага, что M-fe12 в logout, но через E18-флоу мимо logout).
      disconnectConsumer()
      return { ok: true as const }
    }
    return { ok: false as const, error: response?.error }
  }

  // Стоп имперсонации «Вернуться к себе» (E18). Ответ — сессия админа
  // ({user: админ, impersonation: null}). Используется и кнопкой баннера, и при
  // обрыве (см. applyImpersonationEnded).
  async function stopImpersonation() {
    const response = await stopImpersonationReq.execute()
    if (response?.data) {
      applySession(response.data)
      // Cookie вернулся к админу — рвём сокет так же, как на старте имперсонации,
      // иначе он продолжит стримить сотруднику. См. startImpersonation.
      disconnectConsumer()
      return { ok: true as const }
    }
    return { ok: false as const, error: response?.error }
  }

  // Имперсонация оборвалась на бэке (цель деактивирована, §11): access-токен уже
  // обновлён интерсептором (refresh вернул админа), осталось синхронизировать стор
  // — /auth/me теперь отдаёт админа с impersonation: null.
  async function applyImpersonationEnded() {
    await fetchMe()
    // refresh-интерсептор уже вернул cookie админу, но живой сокет всё ещё
    // идентифицирован сотрудником — рвём, чтобы переподключиться под админом.
    // Не в applySession (его дёргает route-guard на каждом fetchMe — был бы
    // reconnect-churn), а точечно здесь, как в start/stopImpersonation.
    disconnectConsumer()
  }

  async function logout() {
    await logoutReq.execute()
    // WS живёт со старым JWT до закрытия вкладки — рвём соединение явно, иначе
    // ActionCable продолжит стримить старому юзеру (следующий getConsumer поднимет
    // новое соединение с новым cookie). См. M-fe12 аудита отказоустойчивости.
    disconnectConsumer()
    clearSession()
  }

  // Сессия потеряна вне явного logout (refresh не помог, см. with_refresh).
  // initialized оставляем true — router-гард не должен снова дёргать fetchMe.
  function markLoggedOut() {
    disconnectConsumer()
    clearSession()
    initialized.value = true
  }

  // Вход по ключу. Без аргумента — usernameless (browser сам предложит ключи).
  // С credentialIds (карточка мультивхода) — allow ограничен ключами запомненного
  // юзера на этом устройстве. options → подпись браузера → верификация на бэке →
  // сессия. Использованный credential id дописываем в карточку (rememberUser).
  // Отмена ceremony (юзер закрыл системный диалог) — getCredential бросает,
  // ловим и возвращаем {ok:false}.
  async function loginByPasskey(credentialIds?: string[]) {
    const optsRes = await passkeyOptionsReq.execute({ credential_ids: credentialIds })
    if (!optsRes?.data) return { ok: false as const, error: optsRes?.error }
    try {
      const credential = await getCredential(optsRes.data)
      const res = await passkeyLoginReq.execute(credential)
      if (res?.data?.user) {
        // remember=false в applySession — единственный upsert делаем здесь с
        // использованным credential id (вход по ключу = доверенное устройство).
        completeSession(res.data.user, false, res.data.capabilities)
        rememberUser(res.data.user, credential.id)
        return { ok: true as const }
      }
      return { ok: false as const, error: res?.error }
    } catch (e) {
      return { ok: false as const, error: e }
    }
  }

  // Passkey как второй фактор: подтверждает начатый пароль-вход (challenge_token).
  async function verifyPasskeyFactor(challengeToken: string) {
    const optsRes = await passkeyOptionsReq.execute({ challenge_token: challengeToken })
    if (!optsRes?.data) return { ok: false as const, error: optsRes?.error }
    try {
      const credential = await getCredential(optsRes.data)
      const res = await passkeyFactorReq.execute(challengeToken, credential)
      if (res?.data?.user) {
        pendingChallenge.value = null
        completeSession(res.data.user, true, res.data.capabilities)
        return { ok: true as const }
      }
      return { ok: false as const, error: res?.error }
    } catch (e) {
      return { ok: false as const, error: e }
    }
  }

  // Частичное обновление текущего юзера (PATCH /users/me).
  // Server возвращает { user, weak_password } → подменяем локальный ref и отдаём
  // флаг слабого пароля (§4.11): короткий/распространённый принимается без
  // блокировки, фронт показывает ненавязчивое предупреждение.
  async function updateMe(patch: UpdateMePatch) {
    const response = await updateMeReq.execute(patch)
    if (response?.data) {
      user.value = response.data.user
      if (!impersonation.value) rememberUser(response.data.user)
      // Свежий Sentry user-context — не обязательно (id не меняется на updateMe),
      // но держит вызов рядом с любой сменой user.value, а не только applySession.
      setSentryUser(response.data.user)
      return { ok: true as const, weakPassword: response.data.weak_password === true }
    }
    return { ok: false as const, error: response?.error }
  }

  return {
    user,
    capabilities,
    loading,
    initialized,
    isAuthenticated,
    viewerTimezone,
    impersonation,
    isImpersonating,
    pendingChallenge,
    rememberedUsers,
    forgetRememberedUser,
    addCredentialToRemembered,
    fetchMe,
    login,
    verifyOtp,
    resendOtp,
    loginByPasskey,
    verifyPasskeyFactor,
    clearChallenge,
    requestPasswordReset,
    updatePassword,
    updateMyTwoFactor,
    startImpersonation,
    stopImpersonation,
    applyImpersonationEnded,
    logout,
    markLoggedOut,
    updateMe,
  }
})
